// Canonical shared web client. Sync to cabinet src/messenger with sync.mjs.
export function mergeWindow(previous, state) {
  const incoming = Array.isArray(state.messages) ? state.messages : [];
  const start = state.windowStartId;
  const retained = start == null ? [] : previous.filter(message => Number(message.id) < Number(start));
  return Array.from(new Map(retained.concat(incoming).map(message => [String(message.id), message])).values())
    .sort((a, b) => Number(a.id) - Number(b.id));
}

export function parseEvents(buffer) {
  const normalized = buffer.replace(/\r\n/g, '\n');
  const boundary = normalized.lastIndexOf('\n\n');
  if (boundary < 0) return { events: [], rest: normalized };
  const events = normalized.slice(0, boundary).split('\n\n').map(block => {
    const lines = block.split('\n');
    return { event: (lines.find(line => line.startsWith('event:')) || '').slice(6).trim(),
      data: lines.filter(line => line.startsWith('data:')).map(line => line.slice(5).trim()).join('\n') };
  });
  return { events, rest: normalized.slice(boundary + 2) };
}

export async function findRevokedThreadIds(ids, request) {
  const known = Array.from(new Set(ids.map(String)));
  const revoked = new Set();
  for (let start = 0; start < known.length; start += 500) {
    const batch = known.slice(start, start + 500);
    const response = await request('/api/messenger/thread-access?ids=' + batch.join(','));
    const allowed = new Set((response.allowedIds || []).map(String));
    batch.forEach(id => { if (!allowed.has(id)) revoked.add(id); });
  }
  return revoked;
}

export function createMessengerClient({ request, baseUrl }) {
  const listeners = new Set();
  let controller = null;
  let retry = null;
  let poll = null;
  let watchdog = null;
  let generation = 0;
  let healthy = false;
  let failures = 0;
  let identity = null;
  let identityEpoch = 0;
  let hiddenListener = null;
  let refreshTimer = null;
  const pendingChats = new Set();
  const statesInFlight = new Map();
  const refreshAgain = new Set();
  const origin = () => new URL(baseUrl || window.location.origin, window.location.origin).origin;
  const shortState = async (listener, force = false) => {
    if (!listener.chatId || !listeners.has(listener)) return;
    const chatId = String(listener.chatId);
    const epoch = identityEpoch;
    try {
      let statePromise = statesInFlight.get(chatId);
      if (statePromise && force) refreshAgain.add(chatId);
      if (!statePromise) {
        statePromise = request('/api/chats/' + chatId + '/state');
        statesInFlight.set(chatId, statePromise);
        statePromise.finally(() => {
          if (statesInFlight.get(chatId) !== statePromise) return;
          statesInFlight.delete(chatId);
          if (refreshAgain.delete(chatId)) listeners.forEach(active => { if (String(active.chatId) === chatId) void shortState(active); });
        }).catch(() => {});
      }
      const state = await statePromise;
      if (epoch === identityEpoch && listeners.has(listener) && listener.onState) listener.onState(state);
    } catch (error) {
      if (epoch === identityEpoch && listeners.has(listener)) {
        if (error.response && [403, 404].includes(error.response.status) && listener.onState) listener.onState({ messages: [], windowStartId: null, reads: [], typing: [], pinnedMessages: [], activeCalls: [] });
        if (listener.onError) listener.onError(error);
      }
    }
  };
  const resync = () => {
    listeners.forEach(listener => {
      void shortState(listener, true);
      if (listener.onInvalidate) listener.onInvalidate(null);
    });
  };
  const invalidate = (ids) => {
    ids.forEach(id => pendingChats.add(String(id)));
    if (refreshTimer) return;
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      const changed = Array.from(pendingChats);
      pendingChats.clear();
      listeners.forEach(listener => {
        if (changed.includes(String(listener.chatId))) void shortState(listener, true);
        if (listener.onInvalidate) listener.onInvalidate(changed);
      });
    }, 150);
  };
  const stopConnection = () => {
    generation++;
    healthy = false;
    clearTimeout(retry); clearTimeout(watchdog);
    if (controller) controller.abort();
    controller = null;
  };
  const connect = async () => {
    if (!listeners.size) return;
    const current = ++generation;
    const abort = new AbortController();
    controller = abort;
    try {
      const ticket = await request('/api/messenger/realtime/session', { method: 'POST', data: {} });
      if (current !== generation) return;
      const url = new URL(ticket.url, origin());
      if (url.origin !== origin() || url.pathname !== '/api/messenger/events' || url.search) throw new Error('untrusted_realtime_url');
      const armWatchdog = () => { clearTimeout(watchdog); watchdog = setTimeout(() => abort.abort(), 45000); };
      armWatchdog();
      const response = await fetch(url.toString(), { credentials: 'omit', signal: abort.signal, headers: { Accept: 'text/event-stream', Authorization: 'Bearer ' + ticket.token } });
      if (!response.ok || !response.body) throw new Error('realtime_unavailable');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (current === generation) {
        const part = await reader.read();
        if (part.done) break;
        armWatchdog();
        buffer += decoder.decode(part.value, { stream: true });
        if (buffer.length > 131072) throw new Error('realtime_event_too_large');
        const parsed = parseEvents(buffer); buffer = parsed.rest;
        for (const event of parsed.events) {
          if (!event.data || current !== generation) continue;
          const data = JSON.parse(event.data);
          if (event.event === 'ready') { healthy = true; failures = 0; }
          if (event.event === 'resync') resync();
          if (event.event === 'invalidate' && Array.isArray(data.chatIds)) invalidate(data.chatIds.filter(id => /^\d+$/.test(String(id))));
        }
      }
    } catch (error) {
      // A staged rollout or interrupted stream falls back to bounded /state requests.
    } finally {
      if (current === generation) {
        healthy = false;
        abort.abort(); clearTimeout(watchdog);
        failures++;
        retry = setTimeout(connect, Math.min(60000, 1500 * Math.pow(2, Math.min(failures, 5))) + Math.random() * 1000);
      }
    }
  };
  return {
    request,
    origin,
    findRevokedThreadIds: ids => findRevokedThreadIds(ids, request),
    setIdentity(userId) {
      const next = String(userId);
      if (identity !== null && identity !== next) { identityEpoch++; stopConnection(); statesInFlight.clear(); refreshAgain.clear(); pendingChats.clear(); resync(); void connect(); }
      identity = next;
    },
    subscribe(listener) {
      listeners.add(listener);
      void shortState(listener);
      if (listeners.size === 1) {
        void connect();
        poll = setInterval(() => { if (!healthy && document.visibilityState !== 'hidden') resync(); }, 15000);
        // Keep the single gateway connection for incoming desktop calls even in
        // a background tab. Visibility gates read receipts and fallback polling.
        hiddenListener = () => { if (document.visibilityState !== 'hidden') resync(); };
        document.addEventListener('visibilitychange', hiddenListener);
      }
      return () => {
        listeners.delete(listener);
        if (!listeners.size) {
          stopConnection(); clearInterval(poll); clearTimeout(refreshTimer);
          document.removeEventListener('visibilitychange', hiddenListener);
          pendingChats.clear(); statesInFlight.clear(); refreshAgain.clear(); refreshTimer = null;
        }
      };
    },
  };
}

export function signedMediaResource(value, allowedOrigin) {
  try {
    const url = new URL(value, allowedOrigin);
    if (url.origin !== allowedOrigin) return null;
    const match = url.pathname.match(/^\/chat-media\/(\d+)\/(original|thumbnail)$/);
    return match ? { id: match[1], variant: match[2], expires: Number(url.searchParams.get('expires') || 0) } : null;
  } catch { return null; }
}

// Native media tags cannot add bearer headers. Recover expired signed URLs through
// the existing authenticated Axios client, keeping the resulting blob in memory.
export function installMediaRecovery(root, client, onError) {
  const urls = new Set();
  const pending = new WeakSet();
  let disposed = false;
  const recover = async (target, resource, download) => {
    if (pending.has(target)) return;
    pending.add(target);
    try {
      const path = '/api/chats/attachments/' + resource.id + (resource.variant === 'thumbnail' ? '/thumbnail' : '/download');
      const blob = await client.request(path, { responseType: 'blob' });
      if (disposed) return;
      const url = URL.createObjectURL(blob); urls.add(url);
      if (download) {
        const link = document.createElement('a'); link.href = url; link.download = target.download || target.textContent.trim() || 'Вложение'; link.click();
      } else {
        const time = Number(target.currentTime || 0); const playing = target.paused === false;
        target.src = url;
        if (typeof target.load === 'function') { target.load(); target.currentTime = time; if (playing) void target.play().catch(() => {}); }
      }
    } catch (error) { if (!disposed && onError) onError('Не удалось открыть вложение. Обновите чат и повторите.'); }
    finally { pending.delete(target); }
  };
  const onMediaError = event => {
    const target = event.target;
    const resource = signedMediaResource(target.currentSrc || target.src || '', client.origin());
    if (resource) void recover(target, resource, false);
  };
  const onClick = event => {
    const link = event.target.closest && event.target.closest('a[href]');
    if (!link) return;
    const resource = signedMediaResource(link.href, client.origin());
    if (resource && resource.expires * 1000 <= Date.now() + 10000) { event.preventDefault(); void recover(link, resource, true); }
  };
  root.addEventListener('error', onMediaError, true); root.addEventListener('click', onClick, true);
  return () => { disposed = true; root.removeEventListener('error', onMediaError, true); root.removeEventListener('click', onClick, true); urls.forEach(url => URL.revokeObjectURL(url)); };
}
